import React, { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';

// Corner on the sender's side is squared where the bubble joins others from the same person
const radius = ({ isUser, $first }) => {
  const joined = '4px';
  const round = '16px';
  // top-left top-right bottom-right bottom-left
  return isUser
    ? `${round} ${$first ? round : joined} ${joined} ${round}`
    : `${$first ? round : joined} ${round} ${round} ${joined}`;
};

// One colour per speaker: client soft blue-grey, Aura white, a 5C agent light green.
// Every bubble has a border: the client platform's dark mode flips the colours, which leaves
// a white bubble black on a near-black background - only the outline still separates them.
const speakerStyle = ({ isUser, $agent }) => {
  if (isUser) {
    return `
      background: #e7edf3;
      border: 1px solid #c3d3e6;
      color: #1b3c66;
    `;
  }
  if ($agent) {
    return `
      background: #e8f5ee;
      border: 1px solid #bfe0cc;
      color: #1f2937;
    `;
  }
  return `
    background: white;
    border: 1px solid #d9e0e8;
    color: #334155;
    box-shadow: 0 1px 2px rgba(0,0,0,0.06);
  `;
};

const BubbleContainer = styled.div`
  max-width: 78%;
  width: fit-content;
  box-sizing: border-box;
  padding: 10px 14px;
  border-radius: ${radius};
  word-wrap: break-word;
  word-break: break-word;
  font-size: 14.5px;
  line-height: 1.45;
  text-align: left;
  overflow-wrap: break-word;

  @media (max-width: 480px) {
    font-size: 14px;
    padding: 9px 12px;
    max-width: 85%;
  }

  @media (max-width: 320px) {
    font-size: 13px;
    padding: 8px 10px;
    max-width: 90%;
  }

  ${speakerStyle}

  /* Ensure proper text rendering */
  font-feature-settings: "liga" 1, "kern" 1;
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  
  /* Smooth transitions for streaming content */
  transition: all 0.1s ease-out;
  
  /* Handle long URLs and text */
  & a {
    word-break: break-all;
    color: #275386;
    text-decoration: underline;
    
    &:hover {
      color: #1f4470;
    }
  }
  
  /* Enhanced HTML content styling for streaming */
  & p {
    margin: 0;
    margin-bottom: 8px;
    
    &:last-child {
      margin-bottom: 0;
    }
    
    /* Handle empty paragraphs during streaming */
    &:empty {
      display: none;
    }
  }
  
  & strong, & b {
    font-weight: 600;
    color: inherit;
  }
  
  & em, & i {
    font-style: italic;
    color: inherit;
  }
  
  & u {
    text-decoration: underline;
    color: inherit;
  }
  
  & br {
    line-height: 1.5;
  }
  
  & ul, & ol {
    margin: 2px 0;
    padding-left: 20px;
    
    /* Responsive list padding */
    @media (max-width: 480px) {
      padding-left: 16px;
    }
  }
  
  & li {
    margin-bottom: 4px;
    
    @media (max-width: 480px) {
      margin-bottom: 2px;
    }
  }
  
  & h1, & h2, & h3, & h4, & h5, & h6 {
    margin: 8px 0;
    color: inherit;
    font-weight: 600;
    
    @media (max-width: 480px) {
      margin: 6px 0;
    }
  }
  
  /* Responsive heading sizes */
  & h1 { 
    font-size: 1.4em; 
    @media (max-width: 480px) { font-size: 1.3em; }
  }
  & h2 { 
    font-size: 1.3em; 
    @media (max-width: 480px) { font-size: 1.25em; }
  }
  & h3 { 
    font-size: 1.2em; 
    @media (max-width: 480px) { font-size: 1.15em; }
  }
  & h4 { 
    font-size: 1.1em; 
    @media (max-width: 480px) { font-size: 1.1em; }
  }
  & h5 { 
    font-size: 1em; 
    @media (max-width: 480px) { font-size: 1em; }
  }
  & h6 { 
    font-size: 0.9em; 
    @media (max-width: 480px) { font-size: 0.9em; }
  }
  
  & blockquote {
    margin: 8px 0;
    padding-left: 12px;
    border-left: 3px solid #cbd5e1;
    opacity: 0.8;
    font-style: italic;
    
    @media (max-width: 480px) {
      margin: 6px 0;
      padding-left: 8px;
      border-left-width: 2px;
    }
  }
  
  & code {
    background: rgba(15, 23, 42, 0.06);
    padding: 2px 4px;
    border-radius: 4px;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 0.9em;
    color: inherit;
    
    @media (max-width: 480px) {
      font-size: 0.85em;
      padding: 1px 3px;
    }
  }
  
  & pre {
    background: rgba(15, 23, 42, 0.04);
    padding: 8px;
    border-radius: 6px;
    overflow-x: auto;
    margin: 8px 0;
    
    @media (max-width: 480px) {
      padding: 6px;
      margin: 6px 0;
      border-radius: 4px;
    }
    
    & code {
      background: none;
      padding: 0;
    }
  }
  
  & table {
    width: 100%;
    border-collapse: collapse;
    margin: 8px 0;
    
    /* Responsive table */
    @media (max-width: 480px) {
      font-size: 0.9em;
      margin: 6px 0;
    }
    
    & th, & td {
      padding: 6px 8px;
      border: 1px solid #e2e8f0;
      text-align: left;
      
      @media (max-width: 480px) {
        padding: 4px 6px;
      }
    }
    
    & th {
      background: rgba(15, 23, 42, 0.04);
      font-weight: 600;
    }
  }
  
  & div {
    /* Handle div elements commonly used in HTML responses */
    margin: 4px 0;
    
    &:first-child {
      margin-top: 0;
    }
    
    &:last-child {
      margin-bottom: 0;
    }
    
    @media (max-width: 480px) {
      margin: 2px 0;
    }
  }
  
  & span {
    /* Preserve inline spans */
    color: inherit;
  }
  
  /* Streaming caret: sits right where the text is arriving (replaces the trailing dots,
     which dropped onto their own line under lists) */
  & .aura-caret {
    display: inline-block;
    width: 2px;
    height: 1.05em;
    margin-left: 2px;
    vertical-align: -0.15em;
    border-radius: 1px;
    background: #275386;
    animation: auraCaretBlink 1s steps(1) infinite;
  }

  @keyframes auraCaretBlink {
    50% { opacity: 0; }
  }

  @media (prefers-reduced-motion: reduce) {
    & .aura-caret { animation: none; }
  }
`;


// ---- Streaming helpers -------------------------------------------------------

// The backend sends no progress events before the first token (often 5-10s of tool calls),
// so the label only reflects elapsed time — it never claims a specific step.
const THINKING_STEPS = [
  [0, 'Thinking'],
  [2500, 'Looking this up'],
  [6000, 'Still working on it'],
  [12000, 'Almost there'],
];

const dotPulse = keyframes`
  0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
  30% { opacity: 1; transform: translateY(-2px); }
`;

const ThinkingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #64748b;
  font-size: 0.9em;

  .dots {
    display: inline-flex;
    gap: 3px;
  }

  .dots span {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #275386;
    animation: ${dotPulse} 1.2s infinite ease-in-out;
  }

  .dots span:nth-child(2) { animation-delay: 0.15s; }
  .dots span:nth-child(3) { animation-delay: 0.3s; }

  @media (prefers-reduced-motion: reduce) {
    .dots span { animation: none; opacity: 0.6; }
  }
`;

const Thinking = () => {
  const [label, setLabel] = useState(THINKING_STEPS[0][1]);
  useEffect(() => {
    const timers = THINKING_STEPS.slice(1).map(([ms, text]) => setTimeout(() => setLabel(text), ms));
    return () => timers.forEach(clearTimeout);
  }, []);
  return (
    <ThinkingRow role="status" aria-live="polite">
      <span className="dots" aria-hidden="true"><span /><span /><span /></span>
      {label}…
    </ThinkingRow>
  );
};

// Count / cut visible characters in an HTML string without ever splitting a tag or entity
const visibleLength = (html) => {
  let count = 0;
  for (let i = 0; i < html.length; i++) {
    if (html[i] === '<') {
      const end = html.indexOf('>', i);
      if (end === -1) break; // partial tag still streaming in
      i = end;
    } else if (html[i] === '&') {
      const end = html.indexOf(';', i);
      if (end !== -1 && end - i < 10) i = end;
      count++;
    } else {
      count++;
    }
  }
  return count;
};

const sliceHtml = (html, n) => {
  let count = 0;
  let i = 0;
  while (i < html.length && count < n) {
    if (html[i] === '<') {
      const end = html.indexOf('>', i);
      if (end === -1) break;
      i = end + 1;
    } else if (html[i] === '&') {
      const end = html.indexOf(';', i);
      i = end !== -1 && end - i < 10 ? end + 1 : i + 1;
      count++;
    } else {
      i++;
      count++;
    }
  }
  return html.slice(0, i); // the browser closes any tags left open
};

// Put the caret inside the last element (e.g. inside the last <li>), not after the list
const withCaret = (html) =>
  html.replace(/((?:\s*<\/[a-zA-Z0-9]+\s*>)*\s*)$/, '<span class="aura-caret"></span>$1');

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Network chunks arrive in uneven bursts (1-30 chars). Reveal them at an even pace that
// speeds up when it falls behind, so text flows instead of jumping.
const useSmoothReveal = (source, isHtmlSource, startHidden) => {
  const total = useMemo(
    () => (isHtmlSource ? visibleLength(source) : source.length),
    [source, isHtmlSource]
  );
  const [shown, setShown] = useState(() =>
    startHidden && !prefersReducedMotion() ? 0 : Number.POSITIVE_INFINITY
  );

  useEffect(() => {
    if (shown >= total) return undefined;
    const frame = requestAnimationFrame(() => {
      // Keep a small buffer (~0.3-0.5s of text) so gaps between network chunks don't show as pauses;
      // the step grows with the backlog, so a large burst still catches up quickly.
      setShown((s) => Math.min(total, s + Math.max(1, Math.ceil((total - s) / 24))));
    });
    return () => cancelAnimationFrame(frame);
  }, [shown, total]);

  const done = shown >= total;
  const visible = done ? source : isHtmlSource ? sliceHtml(source, shown) : source.slice(0, shown);
  return { visible, revealing: !done };
};

const MessageBubble = ({ children, isUser, isAgent = false, isFirstInGroup = true, isHtml = false, isStreaming = false }) => {
  const contentString = typeof children === 'string' ? children : '';
  
  // Enhanced HTML detection that works with streaming content
  const detectHtmlContent = (text) => {
    if (typeof text !== 'string') return false;
    
    // Look for HTML patterns
    const htmlIndicators = [
      /<[a-zA-Z][^>]*>/,    // Opening tags like <p>, <div>, etc.
      /<\/[a-zA-Z][^>]*>/,  // Closing tags like </p>, </div>, etc.
      /<[a-zA-Z][^>]*$/,    // Partial tags at end (streaming)
      /&[a-zA-Z0-9]+;/,     // HTML entities like &amp;, &lt;
      /style\s*=/i,         // Style attributes
      /class\s*=/i,         // Class attributes
    ];
    
    return htmlIndicators.some(pattern => pattern.test(text));
  };
  
  // More aggressive HTML detection for common patterns
  const hasCommonHtmlTags = (text) => {
    if (typeof text !== 'string') return false;
    
    const commonTags = [
      '<p>', '<div>', '<span>', '<strong>', '<b>', '<em>', '<i>', '<u>',
      '<h1>', '<h2>', '<h3>', '<h4>', '<h5>', '<h6>',
      '<ul>', '<ol>', '<li>', '<br>', '<table>', '<tr>', '<td>', '<th>',
      '<blockquote>', '<code>', '<pre>', '<a>', '</p>', '</div>', '</span>',
      '</strong>', '</b>', '</em>', '</i>', '</u>', '</h1>', '</h2>', '</h3>',
      '</h4>', '</h5>', '</h6>', '</ul>', '</ol>', '</li>', '</blockquote>',
      '</code>', '</pre>', '</a>', '</table>', '</tr>', '</td>', '</th>'
    ];
    
    const lowerText = text.toLowerCase();
    return commonTags.some(tag => lowerText.includes(tag));
  };
  
  // Determine if we should render as HTML - be more aggressive
  const shouldRenderAsHtml = isHtml || detectHtmlContent(contentString) || hasCommonHtmlTags(contentString);
  
  // console.log('🔍 MessageBubble HTML Detection:', { 
  //   isHtml, 
  //   isStreaming, 
  //   contentLength: contentString.length,
  //   preview: contentString.substring(0, 100),
  //   detectHtmlContent: detectHtmlContent(contentString),
  //   hasCommonHtmlTags: hasCommonHtmlTags(contentString),
  //   shouldRenderAsHtml,
  //   decision: shouldRenderAsHtml ? '✅ RENDER_AS_HTML' : '❌ RENDER_AS_TEXT'
  // });
  
  // Sanitize the whole message once, then reveal it progressively
  const sanitizedFull = shouldRenderAsHtml
    ? contentString
        // Remove dangerous elements
        .replace(/<script[^>]*>.*?<\/script>/gi, '')
        .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '')
        .replace(/<object[^>]*>.*?<\/object>/gi, '')
        .replace(/<embed[^>]*>/gi, '')
        .replace(/<form[^>]*>.*?<\/form>/gi, '')
        .replace(/<input[^>]*>/gi, '')
        .replace(/<button[^>]*>.*?<\/button>/gi, '')
        .replace(/<link[^>]*>/gi, '')
        .replace(/<meta[^>]*>/gi, '')
        // Remove dangerous attributes
        .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
        .replace(/javascript:/gi, '')
        .replace(/vbscript:/gi, '')
        // Clean up malformed attributes
        .replace(/style\s*=\s*["'][^"']*expression[^"']*["']/gi, '')
    : contentString;

  // Only Aura's live replies animate; history and the user's own messages render at once
  const { visible, revealing } = useSmoothReveal(sanitizedFull, shouldRenderAsHtml, !isUser && isStreaming);
  const showCaret = !isUser && (isStreaming || revealing);

  // Nothing has arrived yet: show a status line instead of an empty bubble
  if (!isUser && isStreaming && !contentString) {
    return (
      <BubbleContainer isUser={false} $agent={isAgent} $first={isFirstInGroup}>
        <Thinking />
      </BubbleContainer>
    );
  }

  if (shouldRenderAsHtml && contentString) {
    return (
      <BubbleContainer 
        isUser={isUser} 
        $agent={isAgent}
        $first={isFirstInGroup}
        isStreaming={isStreaming}
        dangerouslySetInnerHTML={{ __html: showCaret ? withCaret(visible) : visible }}
      />
    );
  }

  return (
    <BubbleContainer isUser={isUser} $agent={isAgent} $first={isFirstInGroup} isStreaming={isStreaming}>
      {(() => {
        // Only render as HTML if it contains complete HTML tags
        const hasCompleteHtml = visible.includes('<') && visible.includes('>') && 
                               visible.match(/<[^>]+>/g) && 
                               visible.includes('</');
        
        if (hasCompleteHtml) {
          // Basic sanitization
          const sanitized = visible
            .replace(/<script[^>]*>.*?<\/script>/gi, '')
            .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '');
          
          return <div dangerouslySetInnerHTML={{ __html: showCaret ? withCaret(sanitized) : sanitized }} />;
        }
        
        // Render as plain text with line breaks
        return (
          <div style={{ whiteSpace: 'pre-wrap' }}>
            {visible}
            {showCaret && <span className="aura-caret" />}
          </div>
        );
      })()}
    </BubbleContainer>
  );
};

export default MessageBubble; 